import { useEffect, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { useLocation } from 'react-router-dom'

import { useInView } from 'motion/react'

import { track } from '@/api/tracking'
import { CookiePreferencesFooterLink } from '@/components/CookiePreferencesFooterLink'
import { OFFICIAL_SALES_PHONE, toOfficialSalesTelHref } from '@/lib/officialContact'
import { cn } from '@/lib/utils'
import { ROUTES } from '@/navigation/constants'
import { type LandingLocale, localizePath } from '@/navigation/locale'
import { useLandingLocale, useLocaleFreePathname } from '@/navigation/useLandingLocale'
import { GuiaRepsolHacemosCocinaLogo } from '@/shared/ui/GuiaRepsolHacemosCocinaLogo'

import BasqueLogo from '../../assets/images/basque-logo.svg?react'
import GOELogo from '../../assets/images/tipi-goe.svg?react'

interface FooterProps {
    landingPage?: string
    className?: string
}

const FOOTER_LINK_CLASS = 'text-sm text-[#E8DFD2] transition-opacity hover:opacity-85'
const FOOTER_LABEL_CLASS = 'm-0 text-[11px] font-bold uppercase tracking-[0.14em] text-[#7A7266]'

const InstagramIcon = () => (
    <svg viewBox='0 0 24 24' fill='none' stroke='#F5EEE4' strokeWidth='2' className='h-[18px] w-[18px]' aria-hidden>
        <rect x='3' y='3' width='18' height='18' rx='5' />
        <circle cx='12' cy='12' r='4' />
        <circle cx='17.5' cy='6.5' r='1' fill='#F5EEE4' stroke='none' />
    </svg>
)

const LinkedInIcon = () => (
    <svg viewBox='0 0 24 24' fill='none' stroke='#F5EEE4' strokeWidth='2' className='h-[18px] w-[18px]' aria-hidden>
        <rect x='3' y='3' width='18' height='18' rx='3' />
        <path d='M8 11v5M8 8v.01M12 16v-5M16 16v-3a2 2 0 0 0-4 0' />
    </svg>
)

const TikTokIcon = () => (
    <svg viewBox='0 0 24 24' fill='none' stroke='#F5EEE4' strokeWidth='2' className='h-[18px] w-[18px]' aria-hidden>
        <path d='M9 12a4 4 0 1 0 4 4V4a5 5 0 0 0 5 5' />
    </svg>
)

const SOCIAL_BUTTON_CLASS =
    'inline-flex h-10 w-10 items-center justify-center rounded-full border border-[rgba(245,238,228,0.15)] bg-[rgba(245,238,228,0.05)] transition-opacity hover:opacity-85'

const SpainFlag = () => (
    <svg viewBox='0 0 3 2' className='block h-[17px] w-[26px] rounded-[3px]' aria-hidden>
        <rect width='3' height='2' fill='#AA151B' />
        <rect y='0.5' width='3' height='1' fill='#F1BF00' />
    </svg>
)

const UkFlag = () => (
    <svg viewBox='0 0 60 40' className='block h-[17px] w-[26px] rounded-[3px]' aria-hidden>
        <rect width='60' height='40' fill='#012169' />
        <path d='M0 0 L60 40 M60 0 L0 40' stroke='#FFF' strokeWidth='8' />
        <path d='M0 0 L60 40 M60 0 L0 40' stroke='#C8102E' strokeWidth='5' />
        <path d='M30 0 V40 M0 20 H60' stroke='#FFF' strokeWidth='13' />
        <path d='M30 0 V40 M0 20 H60' stroke='#C8102E' strokeWidth='7' />
    </svg>
)

const RECOGNITION_LOGO_CLASS = 'block max-h-full max-w-full [&>g>path]:fill-[#E8DFD2]'

export const Footer = ({ landingPage = 'food-cost', className }: FooterProps) => {
    const { t } = useTranslation()
    const locale = useLandingLocale()
    const localeFreePath = useLocaleFreePathname()
    const { search, hash } = useLocation()
    const sectionRef = useRef(null)

    const isInView = useInView(sectionRef)

    useEffect(() => {
        if (isInView) track('Footer', { landing_page: landingPage })
    }, [isInView, landingPage])

    const homeHref = localizePath(ROUTES.home, locale)
    const alternateLocale: LandingLocale = locale === 'es' ? 'en' : 'es'
    const alternateHref = `${localizePath(localeFreePath, alternateLocale)}${search}${hash}`

    const contactLine = (
        <p className='m-0 text-sm leading-relaxed text-[#B8AFA3] md:text-[14px]'>
            {t('footer.contactUsAt', 'Contáctanos al')}{' '}
            <a
                href={toOfficialSalesTelHref()}
                className='font-medium text-[#F4502A] transition-opacity hover:opacity-85'
            >
                {OFFICIAL_SALES_PHONE}
            </a>
        </p>
    )

    const resourcesLinks = (
        <nav aria-label={t('footer.resources', 'Recursos')} className='flex flex-col gap-2.5 text-left md:gap-3'>
            <p className={cn(FOOTER_LABEL_CLASS, 'md:mb-0.5')}>{t('footer.resources', 'Recursos')}</p>
            <a href={localizePath(ROUTES.faqs, locale)} className={FOOTER_LINK_CLASS} title={t('footer.faqs')}>
                {t('footer.faqs')}
            </a>
            <a href={localizePath(ROUTES.news, locale)} className={FOOTER_LINK_CLASS} title={t('footer.news')}>
                {t('footer.news')}
            </a>
            <a href={localizePath(ROUTES.learn, locale)} className={FOOTER_LINK_CLASS} title={t('footer.learn')}>
                {t('footer.learn')}
            </a>
            <a href={localizePath(ROUTES.press, locale)} className={FOOTER_LINK_CLASS} title={t('footer.press')}>
                {t('footer.press')}
            </a>
            <a href='/socios/login' className={FOOTER_LINK_CLASS} title={t('footer.partnersAccess', 'Acceso socios')}>
                {t('footer.partnersAccess', 'Acceso socios')}
            </a>
        </nav>
    )

    const legalLinks = (
        <nav aria-label={t('footer.legal', 'Legal')} className='flex flex-col gap-2.5 text-left md:gap-3'>
            <p className={cn(FOOTER_LABEL_CLASS, 'md:mb-0.5')}>{t('footer.legal', 'Legal')}</p>
            <a
                href={localizePath(ROUTES.termsAndConditions, locale)}
                className={FOOTER_LINK_CLASS}
                title={t('footer.termsAndConditions')}
            >
                {t('footer.termsAndConditions')}
            </a>
            <a
                href={localizePath(ROUTES.privacyPolicy, locale)}
                className={FOOTER_LINK_CLASS}
                title={t('footer.privacyPolicy')}
            >
                {t('footer.privacyPolicy')}
            </a>
            <a
                href={localizePath(ROUTES.cookiePolicy, locale)}
                className={FOOTER_LINK_CLASS}
                title={t('footer.cookiePolicy')}
            >
                {t('footer.cookiePolicy')}
            </a>
            <CookiePreferencesFooterLink className={FOOTER_LINK_CLASS} />
        </nav>
    )

    const recognitionBlock = (
        <div className='flex flex-col gap-4 md:gap-3.5'>
            <div className='flex flex-col items-center gap-2 md:items-start md:gap-1.5'>
                <p className={cn(FOOTER_LABEL_CLASS, 'text-center md:text-left')}>
                    {t('footer.collaboratingTool', 'Herramienta colaboradora')}
                </p>
                <div
                    role='group'
                    aria-label={t('footer.collaborationAriaLabel', 'Hacemos cocina de Guía Repsol')}
                >
                    <GuiaRepsolHacemosCocinaLogo className='text-[15px] text-[#E8DFD2] md:text-sm' />
                </div>
            </div>
            <div className='flex flex-col gap-3 md:gap-2.5'>
                <p className={cn(FOOTER_LABEL_CLASS, 'text-center md:text-left')}>
                    {t('footer.recognizedBy', 'Reconocida y premiada por')}
                </p>
                <div className='flex flex-wrap items-center justify-center gap-[22px] md:justify-start md:gap-5'>
                    <div className='flex h-[46px] w-[100px] items-center justify-center md:h-10 md:w-[90px]'>
                        <GOELogo className={RECOGNITION_LOGO_CLASS} role='img' aria-label='GOe' />
                    </div>
                    <div className='flex h-[46px] w-[100px] items-center justify-center md:h-10 md:w-[90px]'>
                        <BasqueLogo className={RECOGNITION_LOGO_CLASS} role='img' aria-label='Basque Culinary Center' />
                    </div>
                </div>
            </div>
        </div>
    )

    const socialLinks = (
        <div className='flex items-center gap-3'>
            <a
                href='https://www.instagram.com/oidotipi'
                target='_blank'
                rel='noopener noreferrer'
                aria-label={t('footer.followUsOnInstagram', 'Síguenos en Instagram')}
                title={t('footer.followUsOnInstagram', 'Síguenos en Instagram')}
                className={SOCIAL_BUTTON_CLASS}
            >
                <InstagramIcon />
            </a>
            <a
                href='https://www.linkedin.com/company/oidotipi'
                target='_blank'
                rel='noopener noreferrer'
                aria-label={t('footer.followUsOnLinkedIn', 'Síguenos en LinkedIn')}
                title={t('footer.followUsOnLinkedIn', 'Síguenos en LinkedIn')}
                className={SOCIAL_BUTTON_CLASS}
            >
                <LinkedInIcon />
            </a>
            <a
                href='https://www.tiktok.com/@oidotipi'
                target='_blank'
                rel='noopener noreferrer'
                aria-label={t('footer.followUsOnTikTok', 'Síguenos en TikTok')}
                title={t('footer.followUsOnTikTok', 'Síguenos en TikTok')}
                className={SOCIAL_BUTTON_CLASS}
            >
                <TikTokIcon />
            </a>
        </div>
    )

    const languageSwitch = (
        <a
            href={alternateHref}
            className='inline-flex transition-opacity hover:opacity-85'
            title={locale === 'es' ? 'English' : 'Español'}
            aria-label={
                locale === 'es'
                    ? t('footer.switchToEnglish', 'Cambiar idioma a inglés')
                    : t('footer.switchToSpanish', 'Cambiar idioma a español')
            }
        >
            {locale === 'es' ? <UkFlag /> : <SpainFlag />}
        </a>
    )

    return (
        <footer
            ref={sectionRef}
            className={cn(
                'w-full bg-[#17130E] px-[22px] pb-7 pt-10 font-archivo md:px-10 md:pb-10 md:pt-[72px] lg:px-20',
                '[background-image:radial-gradient(ellipse_130%_60%_at_50%_0%,rgba(244,80,42,0.24)_0%,rgba(244,80,42,0.06)_45%,transparent_70%)]',
                'md:[background-image:radial-gradient(ellipse_70%_100%_at_50%_0%,rgba(244,80,42,0.2)_0%,rgba(244,80,42,0.05)_45%,transparent_70%)]',
                className,
            )}
        >
            <div className='mx-auto w-full max-w-[1440px]'>
                <div className='mb-6 flex flex-col md:mb-14 md:flex-row md:items-start md:justify-between md:gap-16'>
                    <div className='mb-[26px] flex flex-col items-center gap-3.5 text-center md:mb-0 md:w-[360px] md:shrink-0 md:items-start md:gap-5 md:text-left'>
                        <a href={homeHref} className='flex items-center' aria-label='Tipi'>
                            <img
                                src='/TIPI-tipografico.webp'
                                alt='Tipi'
                                loading='lazy'
                                decoding='async'
                                className='block h-11 w-auto md:h-10'
                            />
                        </a>
                        <p className='m-0 max-w-[26ch] text-pretty text-sm leading-[1.55] text-[#B8AFA3] md:max-w-[30ch] md:text-[15px] md:leading-[1.6]'>
                            {t('footer.description', 'El fuego te ayuda a cocinar. Ahora, también a ganar dinero.')}
                        </p>
                        <div className='hidden md:contents'>
                            {recognitionBlock}
                            {socialLinks}
                        </div>
                    </div>

                    <div className='mb-[22px] md:hidden'>{recognitionBlock}</div>

                    <div className='mb-6 text-center md:hidden'>{contactLine}</div>

                    <div className='mb-6 flex justify-center gap-9 md:mb-0 md:gap-16'>
                        {resourcesLinks}
                        {legalLinks}
                        <div className='hidden flex-col gap-3 md:flex'>
                            <p className={cn(FOOTER_LABEL_CLASS, 'mb-0.5')}>{t('footer.contact', 'Contacto')}</p>
                            {contactLine}
                        </div>
                    </div>

                    <div className='mb-[22px] flex justify-center md:hidden'>{socialLinks}</div>
                </div>

                <div className='mb-3 flex justify-end md:mb-3.5'>{languageSwitch}</div>

                <div className='flex flex-col items-center gap-3.5 border-t border-[rgba(245,238,228,0.1)] pt-[18px] md:flex-row md:items-center md:justify-between md:gap-6 md:pt-6'>
                    <p className='m-0 text-center text-xs text-[#7A7266] md:text-left md:text-[13px]'>
                        {t('footer.copyright')}
                    </p>
                    <img
                        src='/enisa.webp'
                        alt={t('footer.fundingAlt', 'Financiado por la Unión Europea, Gobierno de España, ENISA')}
                        loading='lazy'
                        decoding='async'
                        className='block h-auto w-full max-w-[300px] rounded-md md:h-11 md:w-auto md:max-w-none'
                    />
                </div>
            </div>
        </footer>
    )
}
